import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { supabase } from '../supabaseClient';
import { Heading, Text, Button } from '../styles/components';
import AssessmentProcessing from './AssessmentProcessing';


const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: ${props => props.theme.spacing.md};
  backdrop-filter: blur(4px);
`;

const ModalContainer = styled.div`
  background-color: ${props => props.theme.colors.backgroundAlt};
  width: 100%;
  max-width: 600px;
  border-radius: ${props => props.theme.radii.lg};
  padding: ${props => props.theme.spacing.xl};
  position: relative;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: ${props => props.theme.shadows.lg};
  border: 1px solid ${props => props.theme.colors.bone};

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: ${props => props.theme.spacing.lg};
    height: 100%;
    max-height: 100vh;
    border-radius: 0;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: ${props => props.theme.spacing.lg};
  right: ${props => props.theme.spacing.lg}; // Fixed prop access
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: ${props => props.theme.colors.textLight};
  transition: color 0.2s;

  &:hover {
    color: ${props => props.theme.colors.terracotta};
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
  margin-top: ${props => props.theme.spacing.lg};
  overflow-y: auto;
  padding-right: 4px; // Prevent scrollbar overlap
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
`;

const Label = styled.label`
  font-weight: 600;
  font-size: ${props => props.theme.typography.sizes.bodySmall};
  color: ${props => props.theme.colors.charcoal};
`;

const Input = styled.input`
  padding: ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.radii.sm};
  font-family: ${props => props.theme.typography.body.fontFamily};
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.terracotta};
    box-shadow: 0 0 0 2px rgba(200, 85, 61, 0.2);
  }
`;

const Select = styled.select`
  padding: ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.radii.sm};
  font-family: ${props => props.theme.typography.body.fontFamily};
  font-size: 1rem;
  background-color: white;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.terracotta};
    box-shadow: 0 0 0 2px rgba(200, 85, 61, 0.2);
  }
`;

const FileInputWrapper = styled.div`
  border: 2px dashed ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.radii.md};
  padding: ${props => props.theme.spacing.lg};
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  background-color: ${props => props.theme.colors.bone};

  &:hover {
    border-color: ${props => props.theme.colors.terracotta};
    background-color: rgba(200, 85, 61, 0.05);
  }

  input {
    display: none;
  }
`;

const ErrorMessage = styled.div`
  color: red;
  font-size: ${props => props.theme.typography.sizes.bodySmall};
  margin-top: ${props => props.theme.spacing.sm};
`;

const STAGES = [
    'Pre-seed',
    'Seed',
    'Series A',
    'Series B',
    'Series C',
    'Crossover',
    'Extension'
];

const STATUSES = [
    'Currently raising',
    'Preparing to raise',
    'Already raised (post-mortem)'
];

const AssessmentModal = ({ isOpen, onClose }) => {
    const navigate = useNavigate();
    const [step, setStep] = useState('form'); // form, processing
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        stage: '',
        status: '',
        file: null
    });
    const [error, setError] = useState('');
    const fileInputRef = useRef(null);

    // Reset state when modal opens
    React.useEffect(() => {
        if (isOpen) {
            setStep('form');
            setFormData(prev => ({ ...prev, file: null }));
            setError('');
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file && file.type === 'application/pdf') {
            setFormData(prev => ({ ...prev, file }));
            setError('');
        } else {
            setError('Please upload a valid PDF file.');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.file) {
            setError('Please upload a pitch deck (PDF).');
            return;
        }

        if (!supabase) {
            setError('Database connection not available.');
            return;
        }

        setStep('processing');
        setError('');

        try {
            // 1. Upload File
            const fileName = `${Date.now()}_${formData.file.name.replace(/[^a-zA-Z0-9.]/g, '_')}`;
            const { data: uploadData, error: uploadError } = await supabase.storage
                .from('decks')
                .upload(fileName, formData.file);

            if (uploadError) throw uploadError;

            // Get public URL (or private if we set up signed URLs, but simpler for now if bucket is public or we use path)
            // Actually, Edge Function can access storage if it has Service Role key.
            // We'll pass the file path to the function.
            const deckPath = uploadData.path;

            // 2. Call Assessment Function (Pass form data directly)
            const { data: analysisData, error: functionError } = await supabase.functions.invoke('assess-deck', {
                body: {
                    name: formData.name,
                    email: formData.email,
                    stage: formData.stage,
                    status: formData.status,
                    deck_path: deckPath
                }
            });

            if (functionError) throw functionError;

            if (functionError) throw functionError;

            // Redirect to results page
            navigate('/assessment-results', {
                state: {
                    results: analysisData,
                    email: formData.email
                }
            });
            onClose();

        } catch (err) {
            console.error('Assessment failed:', err);

            // Try to extract backend error message
            let errorMessage = 'Something went wrong during the assessment. Please try again or contact us directly.';

            if (err && typeof err === 'object') {
                // Check for Supabase FunctionsHttpError body
                if ('context' in err && typeof err.context?.json === 'function') {
                    try {
                        const errorBody = await err.context.json();
                        console.error('Edge Function Error Body:', errorBody);
                        if (errorBody.error) errorMessage = `Analysis Failed: ${errorBody.error}`;
                    } catch (e) {
                        console.error('Could not parse error context', e);
                    }
                } else if (err.message) {
                    // Fallback to error message
                    // If it's a 400/500, message might just be "Edge Function returned a non-2xx status code"
                    // so we prefer the body if possible, but logs will help.
                    console.error('Error Message:', err.message);
                }
            }

            setError(errorMessage);
            setStep('form');
        }
    };

    return (
        <Overlay onClick={onClose}>
            <ModalContainer onClick={e => e.stopPropagation()}>
                <CloseButton onClick={onClose}>&times;</CloseButton>

                {step === 'form' && (
                    <>
                        <Heading as="h2" size="h3" $mb="0.5rem">Assess My Pitch</Heading>
                        <Text size="small">Upload your deck for a strategic AI review. You'll get an instant overview here and a detailed PDF report via email.</Text>

                        <Form onSubmit={handleSubmit}>
                            <FormGroup>
                                <Label>Name</Label>
                                <Input
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    placeholder="Your Name"
                                    required
                                />
                            </FormGroup>

                            <FormGroup>
                                <Label>Email</Label>
                                <Input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    placeholder="your@email.com"
                                    required
                                />
                            </FormGroup>

                            <FormGroup>
                                <Label>Stage</Label>
                                <Select
                                    name="stage"
                                    value={formData.stage}
                                    onChange={handleInputChange}
                                    required
                                >
                                    <option value="">Select Stage</option>
                                    {STAGES.map(s => <option key={s} value={s}>{s}</option>)}
                                </Select>
                            </FormGroup>

                            <FormGroup>
                                <Label>Status</Label>
                                <Select
                                    name="status"
                                    value={formData.status}
                                    onChange={handleInputChange}
                                    required
                                >
                                    <option value="">Select Status</option>
                                    {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                                </Select>
                            </FormGroup>

                            <FormGroup>
                                <Label>Pitch Deck (PDF)</Label>
                                <FileInputWrapper onClick={() => fileInputRef.current?.click()}>
                                    <input
                                        type="file"
                                        accept=".pdf"
                                        ref={fileInputRef}
                                        onChange={handleFileChange}
                                    />
                                    <Text $mb="0">{formData.file ? formData.file.name : 'Click to upload PDF'}</Text>
                                </FileInputWrapper>
                            </FormGroup>

                            {error && <ErrorMessage>{error}</ErrorMessage>}

                            <Button type="submit" style={{ marginTop: '1rem', width: '100%' }}>
                                Assess My Deck
                            </Button>
                        </Form>
                    </>
                )}

                {step === 'processing' && (
                    <AssessmentProcessing />
                )}



            </ModalContainer>
        </Overlay>
    );
};

export default AssessmentModal;
